import { getByID } from '@adornis/baseql/operations/mongo.js';
import { workshopIsTeaser } from '@adornis/bh-buildify/client/nodes/institution-products/helper/renderGroupedProducts.js';
import { teaserBanner } from '@adornis/bh-buildify/client/nodes/institution-products/helper/renderWorkshopCard.js';
import { appointmentCard } from '@adornis/bh-registration/client/bh-prompt-appointments-view.js';
import { productDetailPageBuildifyPreset } from '@adornis/buchungsheld-db/_buildify-presets.js';
import { Workshop } from '@adornis/buchungsheld-db/entities/workshop.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { stateful, useObservable } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { BuildifyLitElement } from '@adornis/html-based-buildify/client/BuildifyLitElement.js';
import { runInsertNode } from '@adornis/html-based-buildify/client/helper/runInsertNode.js';
import { MenuItemGroup, type INodeConfig } from '@adornis/html-based-buildify/client/types.js';
import { isNodeSelection } from '@adornis/html-based-buildify/client/util.js';
import type { HTMLBase } from '@adornis/html-based-buildify/db/HTMLBase.js';
import '@adornis/popover/x-dropdown-multiselection.js';
import '@adornis/popover/x-tooltip';
import { globalLanguageSubject } from '@adornis/translation-core/client/languageSubject.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { consume } from '@lit/context';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { entityContextTokenProvider, provideContextEntity, type ProvideInput } from '../../../util.js';

export const RegistrationProductDetails = stateful(({ productID }: { productID: string }) => {
  const language = useObservable(() => globalLanguageSubject, []);
  const productData = useQuery(
    () =>
      getByID(
        Workshop,
        productID,
      )({
        _id: 1,
        name: 1,
        detailPage: 1,
        translatedDescriptionHTML: {
          text: 1,
          langKey: 1,
        },
        appointments: { name: 1, location: 1, start: 1, end: 1, isNonBlocking: 1 },
        timeframe: { from: 1, isRegistrationPossibleBesidesTimeframe: 1 },
      }),
    [productID],
  );
  const product = productData.value;

  if (productData.loading) return html` loading.. `;

  if (!product || !language) {
    return html` produkt nicht gefunden :( `;
  }

  const _teaserBanner = (() => {
    if (!(product instanceof Workshop)) return nothing;
    const [isTeaser, teaserDate] = workshopIsTeaser(product);
    return teaserBanner({ isTeaser, teaserDate });
  })();

  return html`
    ${_teaserBanner}
    <tr-flex space="md" ${provideContextEntity(Workshop, { $instance: product })}>
      ${unsafeHTML(product.detailPage ?? productDetailPageBuildifyPreset)}
    </tr-flex>
  `;
});

@customElement('bh-workshop-appointments')
export class BHWorkshopAppointments extends BuildifyLitElement<HTMLBase> {
  @consume({ context: entityContextTokenProvider.getContext(Workshop), subscribe: true })
  workshop?: ProvideInput<Workshop>;

  @state() _translation = new TranslationController(this);

  override content() {
    const appointments = this.workshop?.['$instance']?.appointments ?? [];
    if (appointments.length === 0) return html`<i>keine Termine</i>`;

    return html`
      <x-flex space="md">
        <x-h2>${this._translation.translate('termin', 'plural')}</x-h2>
        <x-grid space="xl" ${css({ gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))' })}>
          ${repeat(
            appointments,
            appointment => appointment._id,
            appointment => html` ${appointmentCard({ appointment })} `,
          )}
        </x-grid>
      </x-flex>
    `;
  }
}

// ---------------------------------------------------------------------------------------------------------
// Buldify Configs

export const ProductDetailPageConfig = (property: string): INodeConfig => ({
  node: {
    name: 'product-property-' + property,
    schema: {
      draggable: true,
      group: property === 'translatedDescriptionHTML' ? 'block' : 'inline',
      inline: property === 'translatedDescriptionHTML' ? false : true,
      parseDOM: [
        {
          tag: 'node-workshop-property',
          getAttrs: node => ({
            ['workshop-property']: node.getAttribute('workshop-property'),
            ['style']: node.getAttribute('style'),
          }),
        },
      ],
      attrs: {
        ['workshop-property']: { default: property },
        ['style']: {
          default:
            property === 'translatedDescriptionHTML'
              ? 'display: block'
              : 'display: inline; white-space: collapse; font-size: inherit; font-family: inherit; line-height: inherit',
        },
      },
      toDOM: node => {
        return [
          'node-workshop-property',
          {
            ['workshop-property']: node.attrs['workshop-property'],
            style: node.attrs['style'],
            ['no-content']: 'true',
          },
        ];
      },
    },
  },
  menuItems: [
    {
      group: MenuItemGroup.LAYOUT,
      label: 'Workshop ' + property,
      render: editorView => {
        return html`<x-flex
          horizontal
          space="xxs"
          crossaxis-center
          ${css({ color: 'red', fontSize: '18px', cursor: 'pointer' })}
        >
          <x-icon>title</x-icon> <x-text>${property}</x-text>
        </x-flex>`;
      },
      run: view => runInsertNode('product-property-' + property)(view.state, view),
      shouldVisualize: view => {
        return !isNodeSelection(view.state.selection);
      },
    },
  ],
});

export const WorkshopAppointmentsConfig = () => ({
  node: {
    name: 'workshop-appointments',
    schema: {
      draggable: true,
      group: 'block',
      parseDOM: [
        {
          tag: 'bh-workshop-appointments',
        },
      ],
      toDOM: node => {
        return ['bh-workshop-appointments'];
      },
    },
  },
  menuItems: [
    {
      group: MenuItemGroup.LAYOUT,
      label: 'Workshop Appointments',
      render: editorView => {
        return html`<x-flex
          horizontal
          space="xxs"
          crossaxis-center
          ${css({ color: 'blue', fontSize: '18px', cursor: 'pointer' })}
        >
          <x-icon>table_rows</x-icon> <x-text>Workshop Appointments</x-text>
        </x-flex>`;
      },
      run: view => runInsertNode('workshop-appointments')(view.state, view),
      shouldVisualize: view => {
        return !isNodeSelection(view.state.selection);
      },
    },
  ],
});

@customElement('node-workshop-property')
export class NodeWorkshopProperty extends BuildifyLitElement<HTMLBase> {
  @property({ attribute: 'workshop-property' }) workshopProperty = new RXController(this, '');

  @consume({ context: entityContextTokenProvider.getContext(Workshop), subscribe: true })
  workshop?: ProvideInput<Workshop>;

  @state() private readonly language = new RXController(this, globalLanguageSubject, obs => obs);

  override content({ content, mode }) {
    const workshop = this.workshop?.['$instance'];
    if (!workshop) {
      console.warn('workshop not found');
      return html``;
    }

    const workshopPropertyValue = workshop[this.workshopProperty.value];

    if (this.workshopProperty.value === 'translatedDescriptionHTML') {
      return html`
        <tr-flex ${css({ gap: '1em' })}>
          ${unsafeHTML(workshopPropertyValue?.find(it => it.langKey === this.language.value)?.text)}
        </tr-flex>
      `;
    }
    return html`${workshopPropertyValue ?? ''}`;
  }
}
